/**
 * Active-sink registry (spec 68 §4.3, §11.1).
 *
 * The facade (dispatch.ts) always talks to whatever sink is mounted here.
 * At import time that is the noop sink, so every call is safe with zero
 * config. A boot function swaps in a real adapter; the test harness swaps
 * in a recording sink.
 *
 * The mounted sink lives on `globalThis` under a `Symbol.for` key, not in
 * a module-level `let` (CON-1097). A bundler can evaluate this module more
 * than once in one process: Next.js compiles `instrumentation.ts` and each
 * route layer (app-route, rsc, ssr) as separate module instances, so
 * apps/web shipped five copies of this file. `bootNode` mounted the OTel
 * sink on the instrumentation copy while every `log.*` / `track` / `meter`
 * call in app code read its own copy — still the noop sink — and no SSR
 * log record, product event or facade metric ever left prod-web or
 * prod-admin. Spans looked healthy because the OTel auto-instrumentations
 * register through OTel's own `globalThis` symbol; this is the same
 * mechanism, applied to our sink.
 */
import { noopSink } from './noop.js';
import type { TelemetrySink } from './types.js';

const SINK_KEY = Symbol.for('connect0.telemetry.activeSink');

type SinkHolder = { [SINK_KEY]?: TelemetrySink };

const holder = globalThis as unknown as SinkHolder;

export function getSink(): TelemetrySink {
  return holder[SINK_KEY] ?? noopSink;
}

/** Mount a sink. Returns the previously-active sink (for restore). */
export function setSink(sink: TelemetrySink): TelemetrySink {
  const previous = getSink();
  holder[SINK_KEY] = sink;
  return previous;
}

/** Restore the default noop sink. */
export function resetSink(): void {
  delete holder[SINK_KEY];
}

/**
 * True while no real sink is mounted. Compares by `name` rather than
 * identity: another module copy's `noopSink` is a different object.
 */
export function isNoop(): boolean {
  return getSink().name === 'noop';
}
